import { useState } from 'react';
import { EventBus, useGameStore, useLocalBubbleId, isClaimActive, claimLastDay } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { apiHeaders, sessionInfo, useMembersStore } from '../people/membersStore.js';
import { useRoomClaimsStore } from '../../stores/roomClaimsStore.js';
import { Button, Icon } from '../ds/index.js';

const ERRORS: Record<string, string> = {
  NEED_TWO: 'Il faut être exactement deux dans la salle pour la revendiquer.',
  GUEST_IN_ROOM: 'Un invité ne peut pas revendiquer de salle.',
  ROOM_CLAIMED: 'Cette salle vient d’être revendiquée par un autre duo.',
  ALREADY_CLAIMING: 'L’un de vous a déjà une salle cette semaine. Libère-la d’abord.',
  NOT_CLAIMABLE: 'Cette salle ne peut pas être revendiquée.',
  FORBIDDEN: 'Tu ne peux pas revendiquer de salle.',
};

/**
 * Invite d'une salle revendicable (étage Duos) : à deux dans la salle,
 * « Revendiquer » la réserve au duo jusqu'à la fin du dimanche qui suit ; la
 * salle à soi (ou, pour un admin, celle d'un autre duo) peut être libérée.
 */
export function RoomClaimPrompt() {
  const bubbleId = useLocalBubbleId();
  const bubbleNames = useGameStore((s) => s.bubbleNames);
  const playerBubbleIds = useGameStore((s) => s.playerBubbleIds);
  const claimable = useRoomClaimsStore((s) => (bubbleId ? s.claimable.has(bubbleId) : false));
  const claim = useRoomClaimsStore((s) => (bubbleId ? s.claims.get(bubbleId) : undefined));
  const members = useMembersStore((s) => s.members);
  const [busy, setBusy] = useState(false);

  if (!bubbleId || !claimable) return null;
  const { userId, role, isGuest } = sessionInfo();
  if (isGuest) return null;
  const isAdmin = role === 'OWNER' || role === 'ADMIN';
  const room = bubbleNames.get(bubbleId) ?? 'Cette salle';
  const active = claim && isClaimActive(claim) ? claim : undefined;
  const present = [...playerBubbleIds.values()].filter((b) => b === bubbleId).length;

  async function request(method: 'POST' | 'DELETE') {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(method === 'POST' ? `${API_BASE}/world/room-claims` : `${API_BASE}/world/room-claims/${active!.id}`, {
        method, headers: apiHeaders(method === 'POST'),
        ...(method === 'POST' ? { body: JSON.stringify({ zoneId: bubbleId }) } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({} as { error?: string }));
        EventBus.emit('ui:toast', { message: ERRORS[body.error ?? ''] ?? 'Ça n’a pas marché. Réessaie.', tone: 'danger' });
        return;
      }
      EventBus.emit('ui:toast', method === 'POST'
        ? { message: `${room} est à vous jusqu’à ${claimLastDay(((await res.json()) as { expiresAt: string }).expiresAt)} inclus.`, tone: 'success' }
        : { message: `${room} est de nouveau libre.`, tone: 'info' });
    } catch {
      EventBus.emit('ui:toast', { message: 'Erreur réseau. Réessaie.', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  const name = (id: string) => (id === userId ? 'toi' : members.get(id)?.displayName ?? 'quelqu’un');
  let text: string;
  let action: { label: string; variant: 'primary' | 'secondary'; method: 'POST' | 'DELETE' } | null = null;
  if (active) {
    const mine = active.userIds.includes(userId);
    const duo = active.userIds.filter((id) => id !== userId).map(name).join(' et ');
    text = mine
      ? `Salle à vous, toi et ${duo}, jusqu’à ${claimLastDay(active.expiresAt)} inclus.`
      : `Salle revendiquée par ${active.userIds.map(name).join(' et ')} jusqu’à ${claimLastDay(active.expiresAt)} inclus.`;
    if (mine || isAdmin) action = { label: 'Libérer la salle', variant: 'secondary', method: 'DELETE' };
  } else if (present === 2) {
    text = `${room} est libre : revendique-la à deux jusqu’à dimanche inclus. Elle et son tableau ne s’ouvriront qu’à vous, et votre tableau restera ensuite dans « Mes tableaux blancs ».`;
    action = { label: 'Revendiquer', variant: 'primary', method: 'POST' };
  } else {
    text = `${room} se revendique à deux : il faut être exactement deux dans la salle.`;
  }

  return (
    <div className="pl-float-bottom">
      <div className="pl-pop" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 10px 10px 14px', fontSize: 13 }}>
        <Icon name={active ? 'lock' : 'unlock'} size={16} className="pl-menu-icon" />
        <span style={{ flex: 1 }}>{text}</span>
        {action && (
          <Button variant={action.variant} size="sm" disabled={busy} onClick={() => void request(action.method)}>{action.label}</Button>
        )}
      </div>
    </div>
  );
}
